import React, { useEffect, useRef } from 'react';

// TypeScript interfaces
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: 'small' | 'medium' | 'large' | 'fullscreen';
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  additionalClassName?: string;
}

export const Modal = ({
  isOpen,
  onClose,
  title,
  size = 'medium',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className = '',
  additionalClassName = '',
  children
}: React.PropsWithChildren<ModalProps>) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen && closeOnEscape) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, closeOnEscape, onClose]);

  // Handle body class for overflow
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('app-modal-open');
    } else {
      document.body.classList.remove('app-modal-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('app-modal-open');
    };
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className={`app-modal-overlay ${className}`}
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "app-modal-title" : undefined}
      >
        <div
          ref={modalRef}
          className={`app-modal-content app-modal-${size}${additionalClassName ? ` ${additionalClassName}` : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          {(title || showCloseButton) && (
            <div className="app-modal-header">
              {title && <h2 id="app-modal-title" className="app-modal-title">{title}</h2>}
              {showCloseButton && (
                <button
                  className="app-modal-close-button"
                  onClick={onClose}
                  aria-label="Close modal"
                >
                  Close
                  <svg className="svg-icon" aria-hidden="true" focusable="false">
                    <use xmlnsXlink="ttp://www.w3.org/2000/xlink" xlinkHref="#close-icon" />
                  </svg>
                </button>
              )}
            </div>
          )}

          <div className="app-modal-body">
            {children}
          </div>
        </div>
      </div>
    </>
  );
};